import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PhotoIcon, CheckCircleIcon, SparklesIcon, TrashIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import betslipService from '../../services/betslipService';

// Source options for bet slip origin
const SOURCE_OPTIONS = [
  { value: '', label: 'Select source (optional)' },
  { value: 'my_picks', label: 'My Picks' },
  { value: 'friend', label: 'Friend/Family' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'discord', label: 'Discord' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'website', label: 'Website/Blog' },
  { value: 'other', label: 'Other' },
];

// Steps in the upload flow
const STEP_UPLOAD = 'upload';
const STEP_REVIEW = 'review';
const STEP_SUCCESS = 'success';

function ConfidenceBadge({ confidence }) {
  if (confidence === null || confidence === undefined) {
    return <span className="text-xs px-2 py-0.5 rounded bg-gray-500/20 text-gray-400">No match</span>;
  }
  const pct = Math.round(confidence * 100);
  if (pct >= 80) {
    return <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-500/20 text-green-400">{pct}%</span>;
  }
  if (pct >= 60) {
    return <span className="text-xs font-bold px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">{pct}%</span>;
  }
  return <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-500/20 text-red-400">{pct}%</span>;
}

export default function UploadBetSlipModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [reviewLegs, setReviewLegs] = useState([]);
  const [uploadedBetslip, setUploadedBetslip] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Please upload a valid image file (JPG, PNG, WEBP)';
    }
    if (file.size > 10 * 1024 * 1024) {
      return 'File size must be less than 10MB';
    }
    return null;
  };

  const handleFilesSelected = useCallback((files) => {
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        alert(error);
        return;
      }
    }
    if (fileArray.length > 5) {
      alert('Maximum 5 images allowed per betslip');
      return;
    }
    setSelectedFiles(fileArray);
  }, []);

  // Step 1: Upload images for preview (OCR + matching, no save)
  const handlePreview = useCallback(async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one image');
      return;
    }

    setUploading(true);

    try {
      const now = new Date();
      const betDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const preview = await betslipService.previewBetslipUpload(selectedFiles, betDate);
      setPreviewData(preview);
      setReviewLegs(preview.legs || []);
      setStep(STEP_REVIEW);
    } catch (error) {
      console.error('Preview error:', error);
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to process bet slip. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [selectedFiles]);

  // Step 2: User reviews, edits, removes legs, then confirms
  const handleConfirm = useCallback(async () => {
    if (reviewLegs.length === 0) {
      alert('No legs to save. Add at least one leg.');
      return;
    }

    setSaving(true);

    try {
      const now = new Date();
      const betDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const confirmData = {
        sportsbook: previewData?.sportsbook || 'Unknown',
        betslip_type: previewData?.betslip_type || 'unknown',
        bet_date: betDate,
        total_odds: previewData?.total_odds,
        stake_amount: previewData?.stake_amount,
        potential_payout: previewData?.potential_payout,
        notes: notes || null,
        source: source || null,
        source_url: sourceUrl || null,
        legs: reviewLegs,
      };

      const result = await betslipService.confirmBetslipUpload(selectedFiles, confirmData);
      setUploadedBetslip(result);
      setStep(STEP_SUCCESS);

      if (onSuccess) {
        onSuccess(result);
      }

      // Auto-analyze
      if (result.id) {
        setAnalyzing(true);
        try {
          const analysisResult = await betslipService.analyzeBetslip(result.id);
          setAnalysis(analysisResult);
        } catch (analysisError) {
          console.error('Analysis error:', analysisError);
        } finally {
          setAnalyzing(false);
        }
      }
    } catch (error) {
      console.error('Confirm error:', error);
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to save bet slip. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [reviewLegs, previewData, selectedFiles, notes, source, sourceUrl, onSuccess]);

  // Re-scan: go back to upload step and re-run OCR
  const handleRescan = useCallback(() => {
    setPreviewData(null);
    setReviewLegs([]);
    setStep(STEP_UPLOAD);
  }, []);

  // Remove a leg from review
  const handleRemoveLeg = useCallback((index) => {
    setReviewLegs(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Update a leg field
  const handleUpdateLeg = useCallback((index, field, value) => {
    setReviewLegs(prev => prev.map((leg, i) => {
      if (i !== index) return leg;
      return { ...leg, [field]: value };
    }));
  }, []);

  const handleClose = useCallback(() => {
    setStep(STEP_UPLOAD);
    setNotes('');
    setSource('');
    setSourceUrl('');
    setSelectedFiles([]);
    setPreviewData(null);
    setReviewLegs([]);
    setUploadedBetslip(null);
    setAnalysis(null);
    onClose();
  }, [onClose]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFilesSelected(files);
    }
  }, [handleFilesSelected]);

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFilesSelected(files);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-secondary rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-bg-secondary z-10">
          <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <PhotoIcon className="w-6 h-6 text-accent-blue" />
            {step === STEP_UPLOAD && 'Upload Bet Slip'}
            {step === STEP_REVIEW && 'Review Extracted Bets'}
            {step === STEP_SUCCESS && 'Bet Slip Saved'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-bg-card rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">

          {/* ============ STEP 1: UPLOAD ============ */}
          {step === STEP_UPLOAD && (
            <>
              {/* Source Dropdown */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Source
                </label>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  disabled={uploading}
                >
                  {SOURCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Source URL */}
              {source && source !== 'my_picks' && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Source URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    disabled={uploading}
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this bet..."
                  className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  disabled={uploading}
                />
              </div>

              {/* Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
                  ${dragActive ? 'border-accent-blue bg-accent-blue/10' : 'border-border hover:border-accent-blue/50'}
                  ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <input
                  type="file"
                  id="file-upload-modal"
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handleFileInput}
                  disabled={uploading}
                />
                <label htmlFor="file-upload-modal" className="cursor-pointer">
                  <svg
                    className="mx-auto h-12 w-12 text-text-secondary mb-3"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {uploading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin h-5 w-5 border-2 border-accent-blue border-t-transparent rounded-full mr-2"></div>
                      <p className="text-text-primary font-medium">Scanning bet slip...</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg text-text-primary font-medium mb-1">
                        {dragActive ? 'Drop your bet slip here' : 'Drag and drop your bet slip'}
                      </p>
                      <p className="text-sm text-text-secondary mb-2">or click to browse</p>
                      <p className="text-xs text-text-secondary">Supports JPG, PNG, WEBP up to 10MB</p>
                    </>
                  )}
                </label>
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-secondary">
                    Selected Images ({selectedFiles.length}/5)
                  </label>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-bg-card rounded-lg border border-border"
                      >
                        <span className="text-sm text-text-primary truncate flex-1">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="ml-2 p-1 hover:bg-red-500/20 rounded text-red-400 hover:text-red-300"
                          disabled={uploading}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scan Button */}
              <button
                onClick={handlePreview}
                disabled={uploading || selectedFiles.length === 0}
                className={`
                  w-full py-3 px-4 rounded-lg font-medium transition-all duration-200
                  ${selectedFiles.length > 0 && !uploading
                    ? 'bg-accent-blue hover:bg-accent-blue/80 text-white'
                    : 'bg-bg-card text-text-secondary cursor-not-allowed'
                  }
                `}
              >
                {uploading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Scanning...
                  </div>
                ) : (
                  `Scan ${selectedFiles.length > 0 ? selectedFiles.length : ''} Image${selectedFiles.length !== 1 ? 's' : ''}`
                )}
              </button>

              {/* Info */}
              <div className="p-3 bg-bg-primary/50 rounded-lg border border-border">
                <p className="text-sm text-text-secondary mb-2">
                  Our AI will extract your bets for review before saving:
                </p>
                <div className="grid grid-cols-2 gap-1 text-xs text-text-secondary">
                  <span>- Teams & spreads</span>
                  <span>- Odds & payout</span>
                  <span>- Stake amount</span>
                  <span>- Player props</span>
                </div>
              </div>
            </>
          )}

          {/* ============ STEP 2: REVIEW ============ */}
          {step === STEP_REVIEW && previewData && (
            <>
              {/* Betslip Summary */}
              <div className="p-3 bg-bg-card rounded-lg border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-primary">
                    {previewData.sportsbook} - {previewData.betslip_type}
                  </span>
                  {previewData.extraction_confidence !== null && (
                    <ConfidenceBadge confidence={previewData.extraction_confidence} />
                  )}
                </div>
                <div className="flex gap-4 text-xs text-text-secondary">
                  {previewData.stake_amount && <span>Stake: ${previewData.stake_amount}</span>}
                  {previewData.potential_payout && <span>Payout: ${previewData.potential_payout}</span>}
                  {previewData.total_odds && <span>Odds: {previewData.total_odds > 0 ? '+' : ''}{previewData.total_odds}</span>}
                </div>
              </div>

              {/* Leg Count Mismatch Warning */}
              {previewData.leg_count_mismatch && (
                <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-400">
                    Betslip shows {previewData.expected_leg_count} legs but we extracted {reviewLegs.length}.
                    Please review carefully and remove any incorrect legs.
                  </div>
                </div>
              )}

              {/* Review Legs */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-text-secondary">
                    Extracted Legs ({reviewLegs.length})
                  </label>
                </div>

                {reviewLegs.length === 0 && (
                  <div className="p-4 text-center text-text-secondary text-sm">
                    No legs remaining. Re-scan or close.
                  </div>
                )}

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {reviewLegs.map((leg, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-bg-card rounded-lg border border-border"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {/* Player or Teams */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-text-primary truncate">
                              {leg.player_name || `${leg.away_team || '?'} @ ${leg.home_team || '?'}`}
                            </span>
                            <ConfidenceBadge confidence={leg.match_confidence} />
                          </div>

                          {/* Bet Details */}
                          <div className="text-xs text-text-secondary space-x-2">
                            {leg.sport && <span className="uppercase font-medium">{leg.sport}</span>}
                            {leg.bet_type && <span>{leg.bet_type}</span>}
                            {leg.bet_category && <span className="capitalize">{leg.bet_category}</span>}
                            {leg.selection && <span>{leg.selection}</span>}
                            {leg.line_value !== null && leg.line_value !== undefined && <span>{leg.line_value}</span>}
                            {leg.odds && <span>({leg.odds > 0 ? '+' : ''}{leg.odds})</span>}
                          </div>

                          {/* Match Details */}
                          {leg.player_name && leg.player_team && (
                            <div className="text-xs text-text-secondary/70 mt-1">
                              Team: {leg.player_team}
                              {leg.game_id ? ` | Game matched` : ' | No game match'}
                            </div>
                          )}
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemoveLeg(idx)}
                          className="p-1.5 hover:bg-red-500/20 rounded text-red-400 hover:text-red-300 flex-shrink-0"
                          title="Remove this leg"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Editable Fields (collapsed by default, expand for low confidence) */}
                      {leg.match_confidence !== null && leg.match_confidence < 0.8 && (
                        <div className="mt-2 pt-2 border-t border-border/50 grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-text-secondary">Player</label>
                            <input
                              type="text"
                              value={leg.player_name || ''}
                              onChange={(e) => handleUpdateLeg(idx, 'player_name', e.target.value)}
                              className="w-full px-2 py-1 text-xs bg-bg-primary border border-border rounded text-text-primary"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-text-secondary">Line</label>
                            <input
                              type="number"
                              step="0.5"
                              value={leg.line_value ?? ''}
                              onChange={(e) => handleUpdateLeg(idx, 'line_value', e.target.value ? parseFloat(e.target.value) : null)}
                              className="w-full px-2 py-1 text-xs bg-bg-primary border border-border rounded text-text-primary"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleRescan}
                  disabled={saving}
                  className="flex-1 py-3 px-4 rounded-lg font-medium border border-border text-text-secondary hover:bg-bg-card transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Re-scan
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={saving || reviewLegs.length === 0}
                  className={`
                    flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-200
                    ${reviewLegs.length > 0 && !saving
                      ? 'bg-accent-blue hover:bg-accent-blue/80 text-white'
                      : 'bg-bg-card text-text-secondary cursor-not-allowed'
                    }
                  `}
                >
                  {saving ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    `Confirm & Save (${reviewLegs.length} leg${reviewLegs.length !== 1 ? 's' : ''})`
                  )}
                </button>
              </div>
            </>
          )}

          {/* ============ STEP 3: SUCCESS ============ */}
          {step === STEP_SUCCESS && uploadedBetslip && (
            <div className="space-y-4">
              {/* Success Banner */}
              <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" />
                <div>
                  <p className="text-green-400 font-medium">Bet slip saved successfully!</p>
                  <p className="text-sm text-text-secondary">
                    {uploadedBetslip.legs?.length || 0} leg{uploadedBetslip.legs?.length !== 1 ? 's' : ''} saved
                  </p>
                </div>
              </div>

              {/* Analyzing State */}
              {analyzing && (
                <div className="flex items-center justify-center gap-3 p-4 bg-bg-card rounded-lg border border-border">
                  <div className="animate-spin h-5 w-5 border-2 border-accent-blue border-t-transparent rounded-full"></div>
                  <span className="text-text-primary">
                    <SparklesIcon className="w-5 h-5 inline mr-2 text-accent-blue" />
                    Knewton is analyzing your picks...
                  </span>
                </div>
              )}

              {/* Analysis Results */}
              {analysis && !analyzing && (
                <div className="space-y-3">
                  <div className={`p-3 rounded-lg border ${
                    analysis.summary?.avg_confidence >= 60
                      ? 'bg-green-500/10 border-green-500/30'
                      : analysis.summary?.avg_confidence >= 40
                        ? 'bg-yellow-500/10 border-yellow-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <SparklesIcon className="w-5 h-5 text-accent-blue" />
                      <span className="font-medium text-text-primary">Knewton Analysis</span>
                    </div>
                    <p className="text-sm text-text-secondary">{analysis.summary?.recommendation}</p>
                    {analysis.summary?.avg_confidence && (
                      <p className="text-xs text-text-secondary mt-1">
                        Average confidence: <span className="font-medium text-text-primary">{analysis.summary.avg_confidence}%</span>
                        {' '}({analysis.summary.analyzed_legs}/{analysis.summary.total_legs} legs analyzed)
                      </p>
                    )}
                  </div>

                  {analysis.analysis?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-text-secondary">Leg Analysis:</p>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {analysis.analysis.map((leg, idx) => (
                          <div
                            key={leg.leg_id || idx}
                            className="p-2 bg-bg-card rounded-lg border border-border"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-text-primary truncate flex-1">
                                {leg.player_name || 'Game Bet'}
                              </span>
                              {leg.confidence !== null && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  leg.confidence >= 65
                                    ? 'bg-green-500/20 text-green-400'
                                    : leg.confidence >= 45
                                      ? 'bg-yellow-500/20 text-yellow-400'
                                      : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {leg.confidence}%
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-text-secondary">
                              {leg.bet_category && <span className="capitalize">{leg.bet_category}</span>}
                              {leg.selection && <span> {leg.selection.toUpperCase()}</span>}
                              {leg.line_value && <span> {leg.line_value}</span>}
                            </div>
                            {leg.has_prediction && (
                              <div className="mt-1 text-xs">
                                {leg.ml_prediction && (
                                  <span className="text-accent-blue">ML: {leg.ml_prediction}</span>
                                )}
                                {leg.edge !== null && leg.edge !== undefined && (
                                  <span className={`ml-2 ${leg.edge > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    Edge: {leg.edge > 0 ? '+' : ''}{leg.edge}
                                  </span>
                                )}
                                {leg.agrees_with_prediction !== null && (
                                  <span className={`ml-2 ${leg.agrees_with_prediction ? 'text-green-400' : 'text-red-400'}`}>
                                    {leg.agrees_with_prediction ? '\u2713' : '\u2717'}
                                  </span>
                                )}
                              </div>
                            )}
                            {!leg.has_prediction && leg.message && (
                              <p className="text-xs text-text-secondary/70 mt-1">{leg.message}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Done Button */}
              <button
                onClick={handleClose}
                className="w-full py-3 px-4 rounded-lg font-medium bg-accent-blue hover:bg-accent-blue/80 text-white transition-all duration-200"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
